// MACHINERY V2 (Stroje) — the add-from-stock ROW and its quantity stepper
// (M2.5; the row moved here at the M2.5 review, when PartsAddDialogV2.tsx
// crossed the 300-line cap).
//
// Reference impl: design_handoff_machinery_v3/machinery-profile-parts.jsx
// (`PartsAddDialog`, the row + the − / value / + control); artboard 1011.
// PartsAddDialogV2.tsx owns the search, the picks and the submit; this owns what
// one stock row looks like and how its quantity is bounded.
//
// THE CONTROL CANNOT PRODUCE AN OUT-OF-RANGE VALUE. Both buttons disable at the
// bound and the typed input is clamped by the caller on every change, so
// `1..inStock` is enforced by construction rather than by a validation message
// the user has to read. `max` is `inStockQuantity.value`; a row whose max is
// below 1 cannot be picked at all, so `max >= 1` wherever the stepper renders.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { InternalStockItem } from "../../../../inventory/models/models";
import { PartThumb } from "./partsAtoms";

/** The upper bound of the quantity window. Shared with the dialog, which clamps
 *  against it — one definition, not two. */
export const maxFor = (item: InternalStockItem): number => item.inStockQuantity?.value || 0;

export interface QtyStepperProps {
  value: number;
  max: number;
  onChange: (value: number) => void;
  decreaseLabel: string;
  increaseLabel: string;
}

const stepButton = (disabled: boolean): React.CSSProperties => ({
  width: 24,
  height: 26,
  display: "grid",
  placeItems: "center",
  background: "var(--bg-soft)",
  color: disabled ? "var(--faint)" : "var(--muted)",
  border: 0,
  padding: 0,
  cursor: disabled ? "default" : "pointer",
  font: "inherit",
  fontSize: 13,
  lineHeight: 1,
});

export const QtyStepper: React.FC<QtyStepperProps> = ({
  value,
  max,
  onChange,
  decreaseLabel,
  increaseLabel,
}) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      border: "1px solid var(--border)",
      borderRadius: 7,
      overflow: "hidden",
      flexShrink: 0,
    }}
  >
    <button
      type="button"
      aria-label={decreaseLabel}
      disabled={value <= 1}
      onClick={() => onChange(value - 1)}
      style={stepButton(value <= 1)}
    >
      &minus;
    </button>
    <input
      type="number"
      min={1}
      max={max}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{
        width: 42,
        height: 26,
        textAlign: "center",
        border: 0,
        outline: "none",
        background: "var(--card)",
        fontFamily: "var(--font-mono)",
        fontSize: 12,
        fontWeight: 600,
        color: "var(--ink)",
      }}
    />
    <button
      type="button"
      aria-label={increaseLabel}
      disabled={value >= max}
      onClick={() => onChange(value + 1)}
      style={stepButton(value >= max)}
    >
      +
    </button>
  </span>
);

export interface StockPickRowProps {
  item: InternalStockItem;
  /** undefined = not picked. Any number = picked, at that quantity. */
  quantity?: number;
  onToggle: () => void;
  onQuantity: (value: number) => void;
}

/** One internal-stock row in the add dialog (artboard 1011). */
export const StockPickRow: React.FC<StockPickRowProps> = ({
  item,
  quantity,
  onToggle,
  onQuantity,
}) => {
  const { translate } = useTranslation("myMachinery");
  const max = maxFor(item);
  const on = quantity !== undefined;
  const disabled = max < 1;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 11,
        padding: "8px 11px",
        borderRadius: 8,
        background: on ? "var(--mod-accent-soft)" : "var(--card)",
        border: "1px solid " + (on ? "var(--mod-accent-line)" : "var(--border)"),
        opacity: disabled ? 0.55 : 1,
        minWidth: 0,
      }}
    >
      <input
        type="checkbox"
        checked={on}
        disabled={disabled}
        onChange={onToggle}
        aria-label={item.product?.number}
        style={{ accentColor: "var(--mod-accent)", width: 15, height: 15, flexShrink: 0 }}
      />
      <PartThumb src={item.files?.[0]?.content} size={34} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: "block",
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            fontWeight: 700,
            color: "var(--ink)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.product?.number}
        </span>
        <span style={{ display: "block", fontSize: 11, color: "var(--muted)" }}>
          {item.product?.manufacturer?.name} ·{" "}
          {translate("myMachinery:v2.tabs.parts.inStockN", { count: max })}
        </span>
      </span>
      {on && (
        <QtyStepper
          value={quantity as number}
          max={max}
          onChange={onQuantity}
          decreaseLabel={translate("myMachinery:v2.tabs.parts.qtyDecrease")}
          increaseLabel={translate("myMachinery:v2.tabs.parts.qtyIncrease")}
        />
      )}
    </div>
  );
};

export default QtyStepper;
